import { ArrowLeft, Bike, PersonStanding, Check } from 'lucide-react';

interface FleetTypeSelectionScreenProps {
  onBack: () => void;
  currentFleetType: 'rider' | 'bicycle' | 'leg-fleet';
  onSelect: (fleetType: 'rider' | 'bicycle' | 'leg-fleet') => void;
}

export function FleetTypeSelectionScreen({ onBack, currentFleetType, onSelect }: FleetTypeSelectionScreenProps) {
  const fleetTypes = [
    {
      type: 'rider' as const,
      label: 'Rider',
      subtitle: 'Motorcycle / Scooter',
      icon: Bike,
      color: '#E0001B',
      bgColor: 'bg-[#E0001B]/10',
      borderColor: 'border-[#E0001B]',
      description: 'Long distance deliveries (8km+)',
      requirements: ['Registered motorcycle', 'Valid ID', 'Helmet required'],
      earnings: 'High earnings potential',
    },
    {
      type: 'bicycle' as const,
      label: 'Bicycle',
      subtitle: 'Eco-Friendly Cycling',
      icon: Bike,
      color: '#0066CC',
      bgColor: 'bg-[#0066CC]/10',
      borderColor: 'border-[#0066CC]',
      description: 'Medium distance deliveries (2-8km)',
      requirements: ['Valid ID', 'Personal bicycle', 'Helmet required'],
      earnings: 'Medium earnings potential',
    },
    {
      type: 'leg-fleet' as const,
      label: 'Leg Fleet',
      subtitle: 'Walking / On-Foot',
      icon: PersonStanding,
      color: '#00B277',
      bgColor: 'bg-[#00B277]/10',
      borderColor: 'border-[#00B277]',
      description: 'Hyperlocal deliveries (<2km)',
      requirements: ['Valid ID', 'No vehicle needed', 'Comfortable walking shoes'],
      earnings: 'Quick turnaround potential',
    },
  ];

  const handleSelect = (type: 'rider' | 'bicycle' | 'leg-fleet') => {
    if (window.confirm(`Switch to ${fleetTypes.find(f => f.type === type)?.label} fleet type?\n\nYou'll only see jobs suitable for this vehicle type.`)) {
      onSelect(type);
      onBack();
    }
  };

  return (
    <div className="absolute inset-0 bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-50 rounded-lg transition-colors">
          <ArrowLeft className="w-6 h-6 text-[#111111]" />
        </button>
        <h1 className="text-[18px] text-[#111111]">Select Fleet Type</h1>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mb-4">
          <p className="text-[14px] text-[#6B6B6B] mb-2">
            Choose the fleet type that matches your vehicle. This determines which jobs you'll see.
          </p>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-[8px]">
            <p className="text-[12px] text-amber-800">
              ⚠️ Changing your fleet type will update your available jobs. Make sure you have the required documents and vehicle.
            </p>
          </div>
        </div>

        {/* Fleet Type Cards */}
        <div className="space-y-3">
          {fleetTypes.map((fleet) => {
            const Icon = fleet.icon;
            const isSelected = fleet.type === currentFleetType;

            return (
              <button
                key={fleet.type}
                onClick={() => handleSelect(fleet.type)}
                className={`w-full p-4 rounded-[12px] border-2 transition-all ${
                  isSelected
                    ? `${fleet.borderColor} bg-white shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div
                    className={`w-12 h-12 rounded-full ${fleet.bgColor} flex items-center justify-center shrink-0`}
                  >
                    <Icon className="w-6 h-6" style={{ color: fleet.color }} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-[16px] text-[#111111]">{fleet.label}</h3>
                      {isSelected && (
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: fleet.color }}
                        >
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <p className="text-[12px] text-[#6B6B6B] mb-2">{fleet.subtitle}</p>
                    <p className="text-[14px] text-[#111111] mb-3">{fleet.description}</p>

                    {/* Requirements */}
                    <div className="space-y-1 mb-2">
                      {fleet.requirements.map((req, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full bg-[#6B6B6B]" />
                          <span className="text-[12px] text-[#6B6B6B]">{req}</span>
                        </div>
                      ))}
                    </div>

                    {/* Earnings */}
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded">
                      <span className="text-[10px] text-[#6B6B6B]">{fleet.earnings}</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Bottom Note */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-[12px]">
          <p className="text-[12px] text-blue-800 mb-1">📋 Note</p>
          <p className="text-[12px] text-blue-700">
            You can switch between fleet types at any time. Make sure to update your vehicle information and documents accordingly.
          </p>
        </div>
      </div>
    </div>
  );
}
